import { createSelector } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { baseApi } from "@/src/store/baseApi";
import type { RootState } from "@/src/store";
import type {
  CartItem,
  CartMutationResponseApi,
  CartResponseApi,
  NormalizedCart,
} from "./types";
import { normalizeCartResponse } from "./api";

const CART_TAG = "Cart";
const PRODUCT_FALLBACK_IMAGE = "/Office%20Furniture/image-removebg-preview.png";
const EMPTY_CART_ITEMS: CartItem[] = [];
const EMPTY_VARIANT_IDS: number[] = [];

function createOptimisticCartItem(variantId: number): CartItem {
  return {
    itemId: -variantId,
    variantId,
    parentVariantId: variantId,
    slug: `product-${variantId}`,
    sku: `variant-${variantId}`,
    quantity: 1,
    price: 0,
    discountPrice: 0,
    displayDiscountPrice: 0,
    subtotalBeforeDiscount: 0,
    subtotalAfterDiscount: 0,
    stock: 1,
    isPublic: true,
    isNew: false,
    rating: null,
    image: PRODUCT_FALLBACK_IMAGE,
    hoverImage: PRODUCT_FALLBACK_IMAGE,
    gallery: [PRODUCT_FALLBACK_IMAGE],
    optionValues: [],
    name: "جاري تحديث المنتج...",
  };
}

function applyOptimisticAdd(draft: NormalizedCart, variantId: number) {
  const existingItem = draft.items.find((item) => item.variantId === variantId);

  if (existingItem?.itemId && existingItem.itemId < 0) {
    return;
  }

  if (existingItem) {
    existingItem.quantity += 1;
    existingItem.subtotalBeforeDiscount += existingItem.price;
    existingItem.subtotalAfterDiscount += existingItem.discountPrice;
    draft.total += existingItem.discountPrice;
  } else {
    draft.items.unshift(createOptimisticCartItem(variantId));
  }

  draft.totalItems += 1;
  draft.count = draft.items.length;
  draft.message = null;
}

function applyOptimisticRemove(draft: NormalizedCart, itemId: number) {
  const itemIndex = draft.items.findIndex((item) => item.itemId === itemId);

  if (itemIndex === -1) {
    return;
  }

  const [removedItem] = draft.items.splice(itemIndex, 1);

  draft.totalItems = Math.max(0, draft.totalItems - removedItem.quantity);
  draft.total = Math.max(0, draft.total - removedItem.subtotalAfterDiscount);
  draft.count = draft.items.length;
}

function applyOptimisticUpdateQuantity(
  draft: NormalizedCart,
  itemId: number,
  quantity: number,
) {
  const existingItem = draft.items.find((item) => item.itemId === itemId);

  if (!existingItem) {
    return;
  }

  if (quantity <= 0) {
    applyOptimisticRemove(draft, itemId);
    return;
  }

  const previousQuantity = existingItem.quantity;
  const previousSubtotalAfterDiscount = existingItem.subtotalAfterDiscount;
  const previousSubtotalBeforeDiscount = existingItem.subtotalBeforeDiscount;

  existingItem.quantity = quantity;
  existingItem.subtotalBeforeDiscount = existingItem.price * quantity;
  existingItem.subtotalAfterDiscount = existingItem.discountPrice * quantity;

  draft.totalItems = Math.max(
    0,
    draft.totalItems - previousQuantity + quantity,
  );
  draft.total = Math.max(
    0,
    draft.total -
      previousSubtotalAfterDiscount +
      existingItem.subtotalAfterDiscount,
  );
  draft.count = draft.items.length;
  draft.message = null;

  if (
    previousSubtotalBeforeDiscount === 0 &&
    existingItem.price === 0 &&
    existingItem.discountPrice === 0
  ) {
    existingItem.subtotalBeforeDiscount = 0;
    existingItem.subtotalAfterDiscount = 0;
  }
}

const cartApiWithTags = baseApi.enhanceEndpoints({
  addTagTypes: [CART_TAG],
});

export const cartApi = cartApiWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getCart: builder.query<NormalizedCart, void>({
      query: () => ({
        url: "cart",
        method: "GET",
      }),
      transformResponse: (response: CartResponseApi) =>
        normalizeCartResponse(response),
      providesTags: [{ type: CART_TAG, id: "CURRENT" }],
    }),
    addToCart: builder.mutation<CartMutationResponseApi, { variantId: number }>(
      {
        query: ({ variantId }) => ({
          url: "cart/add",
          method: "POST",
          body: { variant_id: variantId },
        }),
        async onQueryStarted({ variantId }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            cartApi.util.updateQueryData("getCart", undefined, (draft) => {
              applyOptimisticAdd(draft, variantId);
            }),
          );

          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      },
    ),
    removeFromCart: builder.mutation<
      CartMutationResponseApi,
      { itemId: number; variantId?: number; isGuest?: boolean }
    >({
      query: ({ itemId, variantId, isGuest }) => ({
        url: "cart/remove",
        method: "POST",
        body: isGuest ? { item_id: variantId } : { item_id: itemId },
      }),
      async onQueryStarted({ itemId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          cartApi.util.updateQueryData("getCart", undefined, (draft) => {
            applyOptimisticRemove(draft, itemId);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateCartItem: builder.mutation<
      CartMutationResponseApi,
      {
        itemId: number;
        quantity: number;
        variantId?: number;
        isGuest?: boolean;
      }
    >({
      query: ({ itemId, quantity, variantId, isGuest }) => ({
        url: "cart/update",
        method: "POST",
        body: isGuest
          ? { item_id: variantId, quantity }
          : { item_id: itemId, quantity },
      }),
      async onQueryStarted({ itemId, quantity }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          cartApi.util.updateQueryData("getCart", undefined, (draft) => {
            applyOptimisticUpdateQuantity(draft, itemId, quantity);
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    clearCart: builder.mutation<CartMutationResponseApi, void>({
      async queryFn(_arg, _api, _extraOptions, fetchWithBQ) {
        const currentCartResult = await fetchWithBQ({
          url: "cart",
          method: "GET",
        });

        if (currentCartResult.error) {
          return { error: currentCartResult.error as FetchBaseQueryError };
        }

        const currentCart = normalizeCartResponse(
          currentCartResult.data as CartResponseApi,
        );

        for (const item of currentCart.items) {
          const removeResult = await fetchWithBQ({
            url: "cart/remove",
            method: "POST",
            body:
              currentCart.type === "guest"
                ? { item_id: item.variantId }
                : { item_id: item.itemId },
          });

          if (removeResult.error) {
            return { error: removeResult.error as FetchBaseQueryError };
          }
        }

        return {
          data: {
            type: currentCart.type,
            message: "تم تفريغ عربة التسوق",
          },
        };
      },
      invalidatesTags: [{ type: CART_TAG, id: "CURRENT" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useAddToCartMutation,
  useClearCartMutation,
  useGetCartQuery,
  useLazyGetCartQuery,
  useRemoveFromCartMutation,
  useUpdateCartItemMutation,
} = cartApi;

const selectCartQueryState = cartApi.endpoints.getCart.select();
const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
const selectCartQueryResult = (state: RootState) =>
  selectCartQueryState(
    state as unknown as Parameters<typeof selectCartQueryState>[0],
  );

export const selectCartData = createSelector(
  selectCartQueryResult,
  (result) => result.data ?? null,
);

export const selectCartItems = createSelector(
  selectCartData,
  (cart) => cart?.items ?? EMPTY_CART_ITEMS,
);

export const selectCartCount = createSelector(selectCartData, (cart) =>
  Number(cart?.totalItems ?? 0),
);

export const selectCartTotal = createSelector(selectCartData, (cart) =>
  Number(cart?.total ?? 0),
);

export const selectCartVariantIdsSet = createSelector(
  selectCartItems,
  (items) => new Set(items.map((item) => item.variantId)),
);

export const selectIsVariantInCart = (variantId: number) =>
  createSelector(selectCartVariantIdsSet, (variantIdsSet) =>
    variantIdsSet.has(variantId),
  );

export const selectCartItemIdByVariantId = (variantId: number) =>
  createSelector(selectCartItems, (items) => {
    const matchedItem = items.find((item) => item.variantId === variantId);
    return matchedItem?.itemId ?? null;
  });
